import { z } from 'zod';

const newUsersSchema = z.object({
  segment_description: z.string(),
  offerId: z.string(),
});

const activeUsersSchema = z.object({
  segment_description: z.string(),
  offerId: z.string(),
  active_high_frequency_count: z.number().optional(),
  active_n_months_duration: z.number(),
  active_n_months_hf_cap: z.number().optional(),
});

const lapsedUsersSchema = z.object({
  segment_description: z.string(),
  offerId: z.string(),
  lapsed_n_months_duration: z.number(),
  lapsed_n_months_cap: z.number().optional(),
  lapsed_high_frequency_count: z.number().optional(),
});

const sqlTemplateInputSchema = z.object({
  is_active: z.literal(true),
  is_incrementality: z.boolean(),
  only_albert_nn_users: z.boolean(),
  experiment_id: z.string().uuid(),
  merchant_name: z.string(),
  merchant_id: z.string(),
  referring_partners: z.string(),
  user_start_date: z.string().date(),
  user_end_date: z.string().date(),
  segments: z.union([
    z.object({
      new_users: newUsersSchema,
    }),
    z.object({
      active_users: activeUsersSchema,
    }),
    z.object({ lapsed_users: lapsedUsersSchema }),
  ]),
});

export type SQLTemplateInputConfigBase = {
  merchantName: string;
  isIncrementality?: boolean;
  isOnlyAlbertNetNewUsers?: boolean;
  experimentId: string;
  merchantId: string;
  issuerNames?: string[];
  segmentDescription?: string;
  offerIds: string[];
  userDateRange: {
    from: string | Date;
    to: string | Date;
  };
};

type SQLTemplateInputConfigNewUsers = SQLTemplateInputConfigBase & {
  templateType: 'new_users';
  templateFields?: undefined;
};

type SQLTemplateInputConfigActiveUsers = SQLTemplateInputConfigBase & {
  templateType: 'active_users';
  templateFields: {
    transactionCount?: number;
    minEvaluationDurationInMonths: number;
    maxEvaluationDurationInMonths?: number;
  };
};

type SQLTemplateInputConfigLapsedUsers = SQLTemplateInputConfigBase & {
  templateType: 'lapsed_users';
  templateFields: {
    transactionCount?: number;
    minEvaluationDurationInMonths: number;
    maxEvaluationDurationInMonths?: number;
  };
};

export type SQLTemplateInputConfig =
  | SQLTemplateInputConfigNewUsers
  | SQLTemplateInputConfigActiveUsers
  | SQLTemplateInputConfigLapsedUsers;

const isNumber = (value: unknown): value is number =>
  value !== null && value !== undefined && !Number.isNaN(Number(value));

const formatTemplateFields = ({
  templateType,
  templateFields,
}: Partial<SQLTemplateInputConfig>) => {
  if (templateType === 'active_users') {
    const {
      transactionCount,
      minEvaluationDurationInMonths,
      maxEvaluationDurationInMonths,
    } = templateFields || {};
    return {
      active_n_months_duration: minEvaluationDurationInMonths,
      ...(isNumber(transactionCount) && {
        active_high_frequency_count: transactionCount,
      }),
      ...(isNumber(maxEvaluationDurationInMonths) && {
        active_n_months_hf_cap: maxEvaluationDurationInMonths,
      }),
      segment_description: `Users who have made 1-${isNumber(transactionCount) ? transactionCount : 'infinity'} purchases in the last ${minEvaluationDurationInMonths} months, and have made at most ${isNumber(transactionCount) ? transactionCount : 'infinity'} purchases in the last ${isNumber(maxEvaluationDurationInMonths) ? maxEvaluationDurationInMonths : 'infinity'} months`,
    } as const;
  }

  if (templateType === 'lapsed_users') {
    const {
      transactionCount,
      minEvaluationDurationInMonths,
      maxEvaluationDurationInMonths,
    } = templateFields || {};
    return {
      lapsed_n_months_duration: minEvaluationDurationInMonths,
      ...(isNumber(maxEvaluationDurationInMonths) && {
        lapsed_n_months_cap: maxEvaluationDurationInMonths,
      }),
      ...(isNumber(transactionCount) && {
        lapsed_high_frequency_count: transactionCount,
      }),
      segment_description: `Users who have no purchases in the last ${minEvaluationDurationInMonths} months but have made 1-${isNumber(transactionCount) ? transactionCount : 'infinity'} purchases in the last ${isNumber(maxEvaluationDurationInMonths) ? maxEvaluationDurationInMonths : 'infinity'} months`,
    } as const;
  }

  if (templateType === 'new_users') {
    return {
      segment_description: `Users who have never made a purchase with the merchant`,
    } as const;
  }

  return {} as const;
};

// Canadian date format matches ISO dates.
const isoDateFormatter = Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const formatSQLTemplateInput = (config?: Partial<SQLTemplateInputConfig>) => {
  const values = {
    is_active: true,
    only_albert_nn_users: config?.isOnlyAlbertNetNewUsers,
    is_incrementality: config?.isIncrementality,
    experiment_id: config?.experimentId || '',
    merchant_name: config?.merchantName || '',
    merchant_id: config?.merchantId || '',
    referring_partners:
      config?.issuerNames?.length > 0 &&
      `(${config?.issuerNames?.map((issuer) => `'${issuer}'`).join(',') || ''})`,
    user_start_date: config?.userDateRange?.from
      ? isoDateFormatter.format(new Date(config?.userDateRange?.from))
      : '',
    user_end_date: config?.userDateRange?.to
      ? isoDateFormatter.format(new Date(config?.userDateRange?.to))
      : '',
    segments: {
      [config?.templateType || '']: {
        segment_description: config?.segmentDescription || '',
        offerId: `array(${config?.offerIds?.map((offerId) => `'${offerId}'`).join(',') || ''})`,
        ...formatTemplateFields(config || {}),
      },
    },
  } as const;

  const result = sqlTemplateInputSchema.safeParse(values);

  return {
    ...result,
    data: values,
  };
};

export default formatSQLTemplateInput;
